import { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { _getStats } from 'routes/routes';
import { CircularProgress } from '@mui/material';

type Stats = {
    total_count: number;
    country_count: { total_count: number; country_name: string }[];
    device_count: { total_count: number; device_type: string }[];
};

const Dashboard = () => {
    const [stats, setStats] = useState<Stats | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            const data = await _getStats();
            setStats(data.data);
        };
        fetchStats();
    }, []);

    if (!stats) {
        return (
            <div className="flex manage-body f24 font-bold text-[#707070] justify-center items-center">
                <CircularProgress></CircularProgress>
            </div>
        );
    }

    const countryPieOption = {
        tooltip: {
            trigger: 'item'
        },
        legend: {
            orient: 'vertical',
            left: 'left'
        },
        series: [
            {
                name: 'Country',
                type: 'pie',
                radius: '60%',
                data: stats.country_count.map((c) => ({
                    value: c.total_count,
                    name: c.country_name
                })),
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }
        ]
    };

    const deviceBarOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            }
        },
        legend: {
            data: stats.device_count.map((d) => d.device_type.toUpperCase()),
            top: 0
        },
        xAxis: {
            type: 'category',
            data: stats.device_count.map((d) => d.device_type.toUpperCase())
        },
        yAxis: {
            type: 'value'
        },
        series: [
            {
                name: 'Devices',
                type: 'bar',
                barWidth: '50%',
                data: stats.device_count.map((d, index) => ({
                    value: d.total_count,
                    itemStyle: {
                        color:
                            d.device_type.toLowerCase() === 'ios'
                                ? '#8884d8'
                                : '#82ca9d'
                    }
                }))
            }
        ]
    };

    return (
        <div className="p-6 space-y-6 h-full w-full overflow-auto">
            <div>
                <h1 className="text-xl font-bold text-[#707070]">Dashboard</h1>
            </div>

            <div className="grid grid-cols-1 gap-6">
                <div className="bg-white p-6 rounded shadow text-center">
                    <h2 className="text-lg font-semibold text-[#969696] mb-2">
                        Total Users
                    </h2>
                    <p className="text-3xl font-bold text-[#707070]">
                        {stats.total_count}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-4 rounded shadow">
                        <div className="header-text py-8 text-[#969696] font-bold text-lg ">
                            <span>Device Breakdown</span>
                        </div>
                        <ul className="text-gray-700 space-y-1">
                            {stats.device_count.map((device, idx) => (
                                <li key={idx} className="flex justify-between">
                                    <span className="text-md text-[#969696]">
                                        {device.device_type.toUpperCase()}
                                    </span>
                                    <span className="text-md text-[#969696]">
                                        {device.total_count}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-white p-4 rounded shadow">
                        <div className="header-text py-8 text-[#969696] font-bold text-lg ">
                            <span>Country Breakdown</span>
                        </div>
                        <ul className="text-gray-700 space-y-1">
                            {stats.country_count.map((country, idx) => (
                                <li key={idx} className="flex justify-between">
                                    <span className="text-md text-[#969696]">
                                        {country.country_name}
                                    </span>
                                    <span className="text-md text-[#969696]">
                                        {country.total_count}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded shadow">
                    <div className="header-text py-8 text-[#969696] font-bold text-lg ">
                        <span>Users by Country</span>
                    </div>
                    <ReactECharts
                        option={countryPieOption}
                        style={{ height: 300 }}
                    />
                </div>

                <div className="bg-white p-4 rounded shadow">
                    <div className="header-text py-8 text-[#969696] font-bold text-lg ">
                        <span>Users by Device</span>
                    </div>
                    <ReactECharts
                        option={deviceBarOption}
                        style={{ height: 300 }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
